import { Injectable } from '@angular/core';
import { Observable, catchError, of, shareReplay, throwError } from 'rxjs';

export interface ApiCacheOptions {
  forceRefresh?: boolean;
  ttlMs?: number;
}

interface ApiCacheEntry<T = unknown> {
  value?: T;
  request$?: Observable<T>;
  expiresAt?: number;
}

/** Application-wide, in-memory cache for read-only API data and navigation data. */
@Injectable({ providedIn: 'root' })
export class ApiCacheService {
  private readonly entries = new Map<string, ApiCacheEntry>();
  private readonly maxEntries = 200;

  static key(namespace: string, ...parts: unknown[]): string {
    return [namespace, ...parts]
      .map((part) => String(part ?? '').trim().toLowerCase())
      .join(':');
  }

  get<T>(key: string): T | undefined {
    const entry = this.readEntry<T>(key);
    if (!entry || entry.value === undefined) return undefined;
    this.touch(key, entry);
    return entry.value;
  }

  set<T>(key: string, value: T, ttlMs?: number): void {
    this.entries.delete(key);
    this.entries.set(key, { value, expiresAt: this.expiry(ttlMs) });
    this.evictOldestEntries();
  }

  getOrLoad<T>(key: string, loader: () => Observable<T>, options: ApiCacheOptions = {}): Observable<T> {
    if (!options.forceRefresh) {
      const entry = this.readEntry<T>(key);
      if (entry) {
        this.touch(key, entry);
        if (entry.value !== undefined) return of(entry.value);
        if (entry.request$) return entry.request$;
      }
    } else {
      this.entries.delete(key);
    }

    const request$ = loader().pipe(
      catchError((error) => {
        const current = this.entries.get(key);
        if (current?.request$ === request$) this.entries.delete(key);
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    this.entries.set(key, { request$, expiresAt: this.expiry(options.ttlMs) });
    this.evictOldestEntries();
    return request$;
  }

  invalidate(key: string): void {
    this.entries.delete(key);
  }

  invalidatePrefix(prefix: string): void {
    for (const key of this.entries.keys()) {
      if (key.startsWith(prefix)) this.entries.delete(key);
    }
  }

  clear(): void {
    this.entries.clear();
  }

  private readEntry<T>(key: string): ApiCacheEntry<T> | undefined {
    const entry = this.entries.get(key) as ApiCacheEntry<T> | undefined;
    if (!entry) return undefined;
    if (entry.expiresAt !== undefined && entry.expiresAt <= Date.now()) {
      this.entries.delete(key);
      return undefined;
    }
    return entry;
  }

  private touch<T>(key: string, entry: ApiCacheEntry<T>): void {
    this.entries.delete(key);
    this.entries.set(key, entry);
  }

  private expiry(ttlMs?: number): number | undefined {
    return ttlMs && ttlMs > 0 ? Date.now() + ttlMs : undefined;
  }

  private evictOldestEntries(): void {
    while (this.entries.size > this.maxEntries) {
      const oldestKey = this.entries.keys().next().value as string | undefined;
      if (!oldestKey) return;
      this.entries.delete(oldestKey);
    }
  }
}
